/**
 * Garden Bed Calculator — TypeScript entry point.
 * Handles shape selection, preset values, result scrolling, and hidden-iframe print flow.
 */
import { initIframePrint } from './shared/iframe-print';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
    interface Window {
        toggleCircleInputs: (value: string) => void;
        setSpacing: (value: string) => void;
        GardenBedCalculator: {
            setDepth: (value: number, unit: string) => void;
            setSpacing: (value: string) => void;
        };
    }
}

/**
 * Toggle visibility of inputs based on selected bed shape.
 */
function toggleCircleInputs(value: string): void {
    const widthInput = document.getElementById('widthInput');
    const lengthLabel = document.getElementById('lengthLabel');

    if (!widthInput || !lengthLabel) return;

    if (value === 'circular') {
        widthInput.classList.add('d-none');
        lengthLabel.textContent = 'Diameter';
    } else if (value === 'square') {
        widthInput.classList.add('d-none');
        lengthLabel.textContent = 'Side Length';
    } else {
        widthInput.classList.remove('d-none');
        lengthLabel.textContent = 'Length';
    }
}

// Expose to inline onchange handlers in Razor view
window.toggleCircleInputs = toggleCircleInputs;
window.setSpacing = setSpacing;

/**
 * Set depth preset values.
 */
function setDepth(value: number, unit: string): void {
    const depthInput = document.querySelector<HTMLInputElement>('[name="Input.SoilDepth"]');
    const unitSelect = document.querySelector<HTMLSelectElement>('[name="Input.DepthUnit"]');

    if (depthInput) depthInput.value = value.toString();
    if (unitSelect) unitSelect.value = unit;
}

/**
 * Set plant spacing preset values.
 */
function setSpacing(value: string): void {
    if (value) {
        const spacingInput = document.querySelector<HTMLInputElement>('[name="Input.PlantSpacing"]');
        const unitSelect = document.querySelector<HTMLSelectElement>('[name="Input.SpacingUnit"]');

        if (spacingInput) spacingInput.value = value;
        if (unitSelect) unitSelect.value = 'inches';
    }
}

/**
 * Scroll to results after calculation.
 */
function scrollToResults(): void {
    const resultsElement = document.getElementById('garden-results');
    if (resultsElement) {
        window.requestAnimationFrame(() => {
            resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }
}

/**
 * Initialise when the DOM is ready.
 */
function init(): void {
    scrollToResults();

    // Depth preset buttons
    document.querySelectorAll<HTMLElement>('[data-depth]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const depth = parseFloat(btn.dataset.depth ?? '0');
            const unit = btn.dataset.unit ?? 'inches';
            setDepth(depth, unit);
        });
    });

    // Calculate another button
    const resetBtn = document.getElementById('btn-calculate-another');
    if (resetBtn) {
        resetBtn.addEventListener('click', () => {
            window.location.href = window.location.pathname;
        });
    }

    // Wire the hidden-iframe print flow
    initIframePrint({
        formId: 'gbcPrintForm',
        iframeId: 'gbcPrintFrame',
        buttonId: 'gbcPrintBtn',
        readyLabel: '<i class="bi bi-printer me-2" aria-hidden="true"></i>Print Results',
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

// Expose public API for inline handlers
window.GardenBedCalculator = {
    setDepth,
    setSpacing,
};
